import { Reducer } from 'react';
import _ from 'lodash';
import Constants from '../constants';
import ContentConstants from '../contentConstants';
import { ITrendGroup, ITrendSurvey } from '../models';
import * as SurveyTrendGroupAPI from '../services/SurveyTrendGroupAPI';
import * as TrendGroupAPI from '../services/TrendGroupAPI';
import {
	ISurveyAdminTrendGroupsGetFailed,
	ISurveyAdminTrendGroupsGetRequested,
	ISurveyAdminTrendGroupsGetSucceeded,
	ISurveyAdminTrendGroupsIdGetFailed,
	ISurveyAdminTrendGroupsIdGetRequested,
	ISurveyAdminTrendGroupsIdGetSucceeded,
	ISurveyAdminTrendGroupsIdUpdateFailed,
	ISurveyAdminTrendGroupsIdUpdateRequested,
	ISurveyAdminTrendGroupsIdUpdateSucceeded,
	ISurveyAdminTrendSurveysGetFailed,
	ISurveyAdminTrendSurveysGetRequested,
	ISurveyAdminTrendSurveysGetSucceeded,
	ISurveyAdminUpdateTrendGroupId,
	ISurveyAdminUpdateTrendGroups,
} from './Actions';
import { ActionTypes } from './ActionTypes';

export interface ITrendGroupState {
	trendSurveys: ITrendSurvey[];
	isLoadingTrendSurveys: boolean;
	trendGroups: ITrendGroup[];
	selectedTrendGroupId?: string;
	selectedTrendGroupName: string;
	isLoadingTrendGroups: boolean;
	isLoadingTrendGroupId: boolean;
	isSaving: boolean;
}

export const defaultState: ITrendGroupState = {
	trendSurveys: [],
	isLoadingTrendSurveys: false,
	trendGroups: [],
	selectedTrendGroupName: ContentConstants.REPORTING_TRENDS_PREVIOUS_SURVEYS_DEFAULT_SELECTION,
	isLoadingTrendGroups: false,
	isLoadingTrendGroupId: false,
	isSaving: false,
};

export const ActionCreators = {
	GetTrendGroups: (surveyId: number) => (dispatch: any) => {
		dispatch({ type: ActionTypes.SURVEY_ADMIN_TRENDGROUPS_GET_REQUESTED });

		return TrendGroupAPI.get(surveyId)
			.then((trendGroups: ITrendGroup[]) => {
				dispatch({ type: ActionTypes.SURVEY_ADMIN_TRENDGROUPS_GET_SUCCEEDED, payload: trendGroups });
			})
			.catch(() => dispatch({ type: ActionTypes.SURVEY_ADMIN_TRENDGROUPS_GET_FAILED }));
	},
	GetTrendSurveys: (surveyId: number) => (dispatch: any) => {
		dispatch({ type: ActionTypes.SURVEY_ADMIN_TRENDSURVEYS_GET_REQUESTED });

		return SurveyTrendGroupAPI.getTrendSurveys(surveyId)
			.then((trendSurveys: ITrendSurvey[]) => {
				dispatch({ type: ActionTypes.SURVEY_ADMIN_TRENDSURVEYS_GET_SUCCEEDED, payload: trendSurveys });
			})
			.catch(() => dispatch({ type: ActionTypes.SURVEY_ADMIN_TRENDSURVEYS_GET_FAILED }));
	},
	GetSelectedTrendGroupId: (surveyId: number) => (dispatch: any) => {
		dispatch({ type: ActionTypes.SURVEY_ADMIN_TRENDGROUPS_ID_GET_REQUESTED });

		return SurveyTrendGroupAPI.getTrendGroupId(surveyId)
			.then((id: string) => {
				dispatch({ type: ActionTypes.SURVEY_ADMIN_TRENDGROUPS_ID_GET_SUCCEEDED, payload: id });
			})
			.catch(() => {
				dispatch({ type: ActionTypes.SURVEY_ADMIN_TRENDGROUPS_ID_GET_FAILED });
			});
	},
	ChangeTrendGroup: (surveyId: number, trendGroupId: string) => (dispatch: any) => {
		dispatch({ type: ActionTypes.SURVEY_ADMIN_UPDATE_TREND_GROUPS, payload: trendGroupId });

		if (trendGroupId === Constants.EMPTY_GUID) {
			return dispatch({ type: ActionTypes.SURVEY_ADMIN_TRENDSURVEYS_GET_SUCCEEDED, payload: [] });
		} else {
			dispatch({ type: ActionTypes.SURVEY_ADMIN_TRENDSURVEYS_GET_REQUESTED });

			return SurveyTrendGroupAPI.get(surveyId, trendGroupId)
				.then((trendSurveys: ITrendSurvey[]) => {
					dispatch({ type: ActionTypes.SURVEY_ADMIN_TRENDSURVEYS_GET_SUCCEEDED, payload: trendSurveys });
					dispatch({ type: ActionTypes.SURVEY_ADMIN_TRENDGROUPS_UPDATE_TRENDGROUPID, payload: trendGroupId });
				})
				.catch(() => dispatch({ type: ActionTypes.SURVEY_ADMIN_TRENDSURVEYS_GET_FAILED }));
		}
	},
	UpdateSelectedTrendGroupId: (surveyId: number, id: string) => (dispatch: any) => {
		dispatch({ type: ActionTypes.TOAST_PROCESSING_NOTIFICATION, payload: ContentConstants.TOAST_PROCESSING_TEXT });
		dispatch({ type: ActionTypes.SURVEY_ADMIN_TRENDGROUPS_UPDATE_ID_REQUESTED });

		return SurveyTrendGroupAPI.update(surveyId, id)
			.then(() => {
				dispatch({ type: ActionTypes.SURVEY_ADMIN_TRENDGROUPS_UPDATE_ID_SUCCEEDED });
				dispatch({ type: ActionTypes.TOAST_SUCCESS_GENERIC_NOTIFICATION });
			})
			.catch(() => {
				dispatch({ type: ActionTypes.SURVEY_ADMIN_TRENDGROUPS_UPDATE_ID_FAILED });
				dispatch({ type: ActionTypes.TOAST_WARNING_GENERIC_NOTIFICATION });
			});
	},
};

type KnownActions =
	| ISurveyAdminTrendGroupsGetRequested
	| ISurveyAdminTrendGroupsGetSucceeded
	| ISurveyAdminTrendGroupsGetFailed
	| ISurveyAdminTrendSurveysGetRequested
	| ISurveyAdminTrendSurveysGetSucceeded
	| ISurveyAdminTrendSurveysGetFailed
	| ISurveyAdminTrendGroupsIdGetRequested
	| ISurveyAdminTrendGroupsIdGetSucceeded
	| ISurveyAdminTrendGroupsIdGetFailed
	| ISurveyAdminUpdateTrendGroups
	| ISurveyAdminTrendGroupsIdUpdateRequested
	| ISurveyAdminTrendGroupsIdUpdateSucceeded
	| ISurveyAdminTrendGroupsIdUpdateFailed
	| ISurveyAdminUpdateTrendGroupId;

export const reducer: Reducer<ITrendGroupState, KnownActions> = (
	state: ITrendGroupState = defaultState,
	action: KnownActions,
): ITrendGroupState => {
	switch (action.type) {
		case ActionTypes.SURVEY_ADMIN_TRENDGROUPS_GET_REQUESTED:
			return { ...state, isLoadingTrendGroups: true };
		case ActionTypes.SURVEY_ADMIN_TRENDGROUPS_GET_SUCCEEDED:
			return { ...state, isLoadingTrendGroups: false, trendGroups: action.payload };
		case ActionTypes.SURVEY_ADMIN_TRENDGROUPS_GET_FAILED:
			return { ...state, isLoadingTrendGroups: false };

		case ActionTypes.SURVEY_ADMIN_TRENDSURVEYS_GET_REQUESTED:
			return { ...state, isLoadingTrendSurveys: true };
		case ActionTypes.SURVEY_ADMIN_TRENDSURVEYS_GET_SUCCEEDED:
			return { ...state, isLoadingTrendSurveys: false, trendSurveys: action.payload };
		case ActionTypes.SURVEY_ADMIN_TRENDSURVEYS_GET_FAILED:
			return { ...state, isLoadingTrendSurveys: false };

		case ActionTypes.SURVEY_ADMIN_TRENDGROUPS_ID_GET_REQUESTED:
			return { ...state, isLoadingTrendGroupId: true };
		case ActionTypes.SURVEY_ADMIN_TRENDGROUPS_ID_GET_SUCCEEDED:
			return { ...state, isLoadingTrendGroupId: false, selectedTrendGroupId: action.payload };
		case ActionTypes.SURVEY_ADMIN_TRENDGROUPS_ID_GET_FAILED:
			return { ...state, isLoadingTrendGroupId: false };

		case ActionTypes.SURVEY_ADMIN_UPDATE_TREND_GROUPS: {
			const trendGroup = _.find(state.trendGroups, (tg: ITrendGroup) => tg.trendGroupId === action.payload);
			return {
				...state,
				selectedTrendGroupId: action.payload,
				selectedTrendGroupName: trendGroup ? trendGroup.displayText : ContentConstants.REPORTING_TRENDS_PREVIOUS_SURVEYS_DEFAULT_SELECTION,
			};
		}
		case ActionTypes.SURVEY_ADMIN_TRENDGROUPS_UPDATE_ID_REQUESTED:
			return { ...state, isSaving: true };
		case ActionTypes.SURVEY_ADMIN_TRENDGROUPS_UPDATE_ID_SUCCEEDED:
			return { ...state, isSaving: false };
		case ActionTypes.SURVEY_ADMIN_TRENDGROUPS_UPDATE_ID_FAILED:
			return { ...state, isSaving: false };

		case ActionTypes.SURVEY_ADMIN_TRENDGROUPS_UPDATE_TRENDGROUPID:
			return { ...state, selectedTrendGroupId: action.payload };
		default:
			return state;
	}
};
